/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';

/**
 * ContactPreview Component
 * @returns {JSX.Element}
 * @constructor
 */
export function ContactPreview() {
  const { t } = useTranslation('translations');
  const {
    values: {
      firstName,
      lastName,
      gender,
      street,
      houseNumber,
      postCode,
      city,
      phone,
      email,
      dateOfBirth,
      paymentChoice,
      iban,
      rateBundleTerm,
      accountOwner,
      cardNumber,
    },
  } = useFormikContext();

  /**
   * Map gender to salutation
   */
  const getSalutation = () => {
    switch (gender) {
      case 'MALE':
        return t('salutationMale');
      case 'FEMALE':
        return t('salutationFemale');
      default:
        return t('salutationDivers');
    }
  };

  return (
    <Grid container mt={4}>
      <Grid item xs={12}>
        <Typography
          sx={{
            fontWeight: 'bold',
            letterSpacing: '1px',
            textTransform: 'uppercase',
            color: '#49ad33',
          }}
        >
          {t('contactPreviewHeader')}
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mt={2}>
          {getSalutation()}
          <br />
          {firstName}
          {' '}
          {lastName}
        </Box>
        <Box mt={2}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {t('address')}
          </Typography>
          {street}
          {' '}
          {houseNumber}
          {' '}
          <br />
          {postCode}
          {' '}
          {city}
          {' '}
          <br />
          {t('phone')}
          :
          {' '}
          {phone}
          {' '}
          <br />
          {t('email')}
          :
          {' '}
          {email}
        </Box>
        <Box mt={2}>
          <Typography key="Simon" sx={{ fontWeight: 'bold' }}>
            {t('dateOfBirth')}
          </Typography>
          {dayjs(dateOfBirth)
            .format('YYYY-MM-DD')}
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <Box mt={2}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {paymentChoice === 'DIRECT_DEBIT' ? t('directDebitAccountOwner') : t('creditCardAccountOwner')}
          </Typography>
          {accountOwner}
        </Box>
        <Box mt={2}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {paymentChoice === 'DIRECT_DEBIT' ? t('paymentDirectDebit') : t('paymentCreditCard')}
          </Typography>
          {paymentChoice === 'DIRECT_DEBIT' ? iban : cardNumber}
        </Box>
        <Box mt={2}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {t('contractStart')}
          </Typography>
          {rateBundleTerm.defaultContractStartDate}
        </Box>
        <Box mt={2}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {t('trainingStart')}
          </Typography>
          {rateBundleTerm.contractStartDateOfUse}
        </Box>
      </Grid>
    </Grid>
  );
}
